// src/lib/logging/build-logger.ts
var BuildLogger = class {
  buildId = null;
  projectId = null;
  /**
   * Set correlation IDs for the current build
   * Call this at the start of each build to enable correlation tracking
   */
  setBuildContext(buildId, projectId) {
    this.buildId = buildId;
    this.projectId = projectId;
    this.log("debug", "runner", `Build context set: ${buildId} / ${projectId}`);
  }
  /**
   * Clear correlation IDs after build completes
   */
  clearBuildContext() {
    this.log("debug", "runner", "Build context cleared");
    this.buildId = null;
    this.projectId = null;
  }
  /**
   * Core logging method - creates structured log entries
   * Public for custom logging needs
   */
  log(level, context, message, data) {
    if (process.env.SILENT_MODE === "1") {
      return;
    }
    if (level === "info" || level === "debug") {
      if (process.env.DEBUG_BUILD === "0") {
        return;
      }
    }
    const entry = {
      timestamp: (/* @__PURE__ */ new Date()).toISOString(),
      level,
      context,
      buildId: this.buildId ?? void 0,
      projectId: this.projectId ?? void 0,
      message,
      data
    };
    const prefix = `[${context}]`;
    const icon = {
      debug: "\u{1F50D}",
      info: "\u{1F4CB}",
      warn: "\u26A0\uFE0F ",
      error: "\u274C"
    }[level];
    const logFn = level === "error" ? console.error : level === "warn" ? console.warn : console.log;
    if (data && Object.keys(data).length > 0) {
      logFn(`${icon} ${prefix} ${message}`, data);
    } else {
      logFn(`${icon} ${prefix} ${message}`);
    }
  }
  /**
   * Orchestrator-specific logging methods
   */
  orchestrator = {
    newProject: (operationType) => this.log("info", "orchestrator", `NEW PROJECT (operationType: ${operationType})`),
    existingProject: (operationType) => this.log("info", "orchestrator", `EXISTING PROJECT (operationType: ${operationType})`),
    templateProvided: (templateName, templateId, framework) => this.log("info", "orchestrator", `Frontend provided template: ${templateName}`, {
      templateId,
      framework
    }),
    templateSelecting: (method) => this.log("info", "orchestrator", `Template selection: ${method}`),
    templateSelected: (templateName, templateId) => this.log("info", "orchestrator", `Selected template: ${templateName}`, { templateId }),
    templateDownloading: (templateName, repository, target) => this.log("info", "orchestrator", `Downloading template: ${templateName}`, {
      repository,
      target
    }),
    templateDownloaded: (templateName, path, fileTreeSize) => this.log("info", "orchestrator", `Template downloaded: ${templateName}`, {
      path,
      fileTreeSize
    }),
    catalogPrepared: (catalogSize) => this.log("info", "orchestrator", `Template catalog prepared (${catalogSize} chars)`, {
      catalogSize
    }),
    systemPromptGenerated: (size) => this.log("info", "orchestrator", `System prompt generated (${size} chars)`, { size }),
    orchestrationComplete: (data) => this.log("info", "orchestrator", "Orchestration complete", data),
    error: (message, error) => this.log("error", "orchestrator", message, {
      error: error instanceof Error ? error.message : String(error)
    })
  };
  /**
   * Message transformer-specific logging methods
   */
  transformer = {
    todoListFound: () => this.log("debug", "transformer", "Found Codex task list, parsing..."),
    todoListParsed: (todoCount, completed, inProgress, pending) => this.log("info", "transformer", `Parsed ${todoCount} todos`, {
      completed,
      inProgress,
      pending
    }),
    todoListInvalidFormat: (expected, got) => this.log("error", "transformer", "Invalid todo format from Codex", {
      expected,
      got: JSON.stringify(got).substring(0, 200)
    }),
    todoListParseError: (error, rawJson) => this.log("error", "transformer", "Failed to parse Codex todolist", {
      error: String(error),
      rawJson: rawJson.substring(0, 300)
    }),
    todoListRemoved: () => this.log("debug", "transformer", "Removed task list from chat text"),
    toolStarted: (toolName, toolId) => this.log("debug", "transformer", `Tool started: ${toolName}`, { toolName, toolId }),
    toolCompleted: (toolName, toolId) => this.log("debug", "transformer", `Tool completed: ${toolName}`, { toolName, toolId }),
    pathViolationWarning: (toolName, path, workspace) => this.log("warn", "transformer", `Path outside workspace: ${path}`, {
      toolName,
      path,
      workspace
    }),
    desktopPathDetected: (path) => this.log("error", "transformer", `DESKTOP PATH DETECTED - Likely hallucinated: ${path}`, { path })
  };
  /**
   * Codex query-specific logging methods
   */
  codexQuery = {
    promptBuilding: (workingDirectory, systemPromptSize, userPromptSize) => this.log("info", "codex-query", "Building Codex prompt", {
      workingDirectory,
      systemPromptSize,
      userPromptSize
    }),
    threadStarting: () => this.log("info", "codex-query", "Starting Codex thread (multi-turn)"),
    turnStarted: (turnNumber, maxTurns, promptSize) => this.log("info", "codex-query", `\u2550\u2550\u2550 Turn ${turnNumber}/${maxTurns} \u2550\u2550\u2550`, {
      turnNumber,
      maxTurns,
      promptSize
    }),
    taskListExtracted: () => this.log("info", "codex-query", "Task list extracted and updated"),
    taskListStatus: (completed, inProgress, pending, total) => this.log("info", "codex-query", `Tasks: ${completed} completed | ${inProgress} in_progress | ${pending} pending (total: ${total})`, {
      completed,
      inProgress,
      pending,
      total
    }),
    taskListTask: (index, content, status, icon) => this.log("debug", "codex-query", `  ${icon} ${index + 1}. ${content}`, { status }),
    taskListParseError: (error, rawContent) => this.log("error", "codex-query", "PARSE ERROR: Could not parse task list JSON", {
      error: String(error),
      rawContent: rawContent.substring(0, 200)
    }),
    taskListMissing: (turnNumber) => this.log("warn", "codex-query", `WARNING: No <start-todolist> tags found in Turn ${turnNumber}`, {
      turnNumber
    }),
    turnComplete: (turnNumber, hadToolCalls, messageLength) => this.log("info", "codex-query", `Turn ${turnNumber} complete`, {
      hadToolCalls,
      messageLength
    }),
    tasksComplete: (completed, total) => this.log("info", "codex-query", `Task status: ${completed}/${total} completed`, {
      completed,
      total
    }),
    allComplete: () => this.log("info", "codex-query", "\u2705 All MVP tasks complete!"),
    allTasksComplete: () => this.log("info", "codex-query", "\u2705 All MVP tasks complete!"),
    taskCompleteDetected: () => this.log("info", "codex-query", "\u2705 Task complete (detected completion signal)"),
    continuePrompting: (reason) => this.log("warn", "codex-query", `No tools used but not done - ${reason}`),
    continuing: () => this.log("info", "codex-query", "\u23ED\uFE0F  Continuing to next turn (had tool calls)"),
    loopExited: (turnCount, maxTurns) => this.log("info", "codex-query", `EXITED WHILE LOOP after ${turnCount} turns`, {
      turnCount,
      maxTurns
    }),
    sessionComplete: (turnCount) => this.log("info", "codex-query", `Session complete after ${turnCount} turns`, { turnCount }),
    error: (message, error) => this.log("error", "codex-query", message, {
      error: error instanceof Error ? error.message : String(error)
    })
  };
  /**
   * Claude query-specific logging methods
   */
  claudeQuery = {
    queryStarted: (model, cwd, maxTurns) => this.log("info", "claude-query", `Starting Claude query (${model})`, {
      cwd,
      maxTurns
    }),
    error: (message, error) => this.log("error", "claude-query", message, {
      error: error instanceof Error ? error.message : String(error)
    })
  };
  /**
   * Runner-specific logging methods
   */
  runner = {
    workspaceRoot: (path) => this.log("info", "runner", `Workspace root: ${path}`, { path }),
    commandReceived: (commandType, projectId) => this.log("info", "runner", `Received command: ${commandType}`, { commandType, projectId }),
    buildOperation: (operationType, projectSlug, agentId) => this.log("info", "runner", `Build operation: ${operationType}`, {
      operationType,
      projectSlug,
      agentId
    }),
    templateProvided: (templateId) => this.log("info", "runner", `Template provided by frontend: ${templateId}`, { templateId }),
    buildStreamCreated: () => this.log("info", "runner", "Build stream created, starting to process chunks..."),
    firstChunkReceived: (agentLabel) => this.log("info", "runner", `First chunk received from ${agentLabel}`, { agentLabel }),
    streamEnded: (chunkCount) => this.log("info", "runner", `Stream ended after ${chunkCount} chunks`, { chunkCount }),
    buildCompleted: (projectId) => this.log("info", "runner", `Build completed successfully`, { projectId }),
    buildFailed: (error) => this.log("error", "runner", `Build failed: ${error}`, { error }),
    portDetected: (port) => this.log("info", "runner", `Port detected: ${port}`, { port }),
    tunnelCreated: (port, tunnelUrl) => this.log("info", "runner", `Tunnel created: ${tunnelUrl} \u2192 localhost:${port}`, {
      port,
      tunnelUrl
    }),
    error: (message, error, context) => this.log("error", "runner", message, {
      error: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : void 0,
      ...context
    })
  };
  /**
   * Build stream-specific logging (tool calls, text, etc.)
   */
  build = {
    agentText: (agentLabel, text) => {
      const truncated = text.length > 200 ? text.slice(0, 200) + "..." : text;
      this.log("debug", "build", `${agentLabel}: ${truncated}`, {
        agentLabel,
        textLength: text.length
      });
    },
    agentThinking: (thinking) => {
      const truncated = thinking.length > 300 ? thinking.slice(0, 300) + "..." : thinking;
      this.log("debug", "build", `Thinking: ${truncated}`, {
        thinkingLength: thinking.length
      });
    },
    toolCalled: (toolName, toolId, inputSize) => this.log("info", "build", `Tool called: ${toolName} (${toolId})`, {
      toolName,
      toolId,
      inputSize
    }),
    toolResult: (toolId, outputSize, isError) => this.log(isError ? "error" : "info", "build", `Tool result (${toolId})`, {
      toolId,
      outputSize,
      isError
    }),
    runCommandDetected: (runCommand) => this.log("info", "build", `Detected runCommand: ${runCommand}`, { runCommand })
  };
  /**
   * WebSocket-specific logging methods
   */
  websocket = {
    serverCreated: (instanceId) => this.log("info", "websocket", `WebSocket server instance created`, { instanceId }),
    serverInitialized: (path, runnerPath) => this.log("info", "websocket", `Server initialized`, { path, runnerPath }),
    clientConnected: (clientId, projectId, sessionId) => this.log("info", "websocket", `Client connected: ${clientId}`, { clientId, projectId, sessionId }),
    clientDisconnected: (clientId) => this.log("info", "websocket", `Client disconnected: ${clientId}`, { clientId }),
    clientSubscribed: (clientId, projectId) => this.log("info", "websocket", `Client subscribed to project: ${projectId}`, { clientId, projectId }),
    clientTimeout: (clientId) => this.log("warn", "websocket", `Client timeout: ${clientId}`, { clientId }),
    runnerConnected: (runnerId) => this.log("info", "websocket", `Runner connected: ${runnerId}`, { runnerId }),
    runnerDisconnected: (runnerId, code) => this.log("info", "websocket", `Runner disconnected: ${runnerId}`, { runnerId, code }),
    runnerNotConnected: (runnerId, commandType) => this.log("warn", "websocket", `Cannot send command to runner ${runnerId}: not connected`, {
      runnerId,
      commandType
    }),
    runnerAuthRejected: () => this.log("warn", "websocket", `Runner connection rejected: invalid auth`),
    runnerAuthMissing: () => this.log("error", "websocket", `RUNNER_SHARED_SECRET is not configured`),
    runnerStaleRemoved: (runnerId) => this.log("info", "websocket", `Removing stale runner connection: ${runnerId}`, { runnerId }),
    commandSent: (runnerId, commandType, traceAttached) => this.log("debug", "websocket", `Sent command to runner: ${commandType}`, {
      runnerId,
      commandType,
      traceAttached
    }),
    eventReceived: (runnerId, eventType) => this.log("debug", "websocket", `Received event from runner: ${eventType}`, {
      runnerId,
      eventType
    }),
    broadcastToolCall: (toolName, toolState, subscriberCount) => this.log("info", "websocket", `Broadcasting planning tool: ${toolName} (state=${toolState})`, {
      toolName,
      toolState,
      subscriberCount
    }),
    broadcastBuildComplete: (projectId, sessionId, subscriberCount) => this.log("info", "websocket", `Broadcasting build-complete`, {
      projectId,
      sessionId,
      subscriberCount
    }),
    unknownUpgradePath: (pathname) => this.log("warn", "websocket", `Unknown upgrade path: ${pathname}`, { pathname }),
    shutdown: () => this.log("info", "websocket", `Shutting down server...`),
    shutdownComplete: () => this.log("info", "websocket", `Server shut down`),
    error: (message, error, context) => this.log("error", "websocket", message, {
      error: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : void 0,
      ...context
    })
  };
  /**
   * Port allocator-specific logging methods
   */
  portAllocator = {
    portAllocated: (port, projectId) => this.log("info", "port-allocator", `Port allocated: ${port}`, { port, projectId }),
    portReleased: (port, projectId) => this.log("info", "port-allocator", `Port released: ${port}`, { port, projectId }),
    portInUse: (port) => this.log("warn", "port-allocator", `Port ${port} is already in use`, { port }),
    portRangeExhausted: (minPort, maxPort) => this.log("error", "port-allocator", `No available ports in range ${minPort}-${maxPort}`, {
      minPort,
      maxPort
    }),
    portConflict: (port, projectId, existingProjectId) => this.log("warn", "port-allocator", `Port ${port} conflict detected`, {
      port,
      projectId,
      existingProjectId
    }),
    allocationsCleared: (count) => this.log("info", "port-allocator", `Cleared ${count} port allocations`, { count }),
    error: (message, error, context) => this.log("error", "port-allocator", message, {
      error: error instanceof Error ? error.message : String(error),
      ...context
    })
  };
  /**
   * Process manager-specific logging methods
   */
  processManager = {
    processStarting: (projectId, command, cwd) => this.log("info", "process-manager", `Starting process: ${command}`, {
      projectId,
      command,
      cwd
    }),
    processStarted: (projectId, pid) => this.log("info", "process-manager", `Process started`, { projectId, pid }),
    processOutput: (projectId, output) => this.log("debug", "process-manager", `Process output: ${output.substring(0, 100)}`, {
      projectId,
      outputLength: output.length
    }),
    processError: (projectId, error) => this.log("error", "process-manager", `Process error: ${error}`, { projectId, error }),
    processExited: (projectId, code, signal) => this.log("info", "process-manager", `Process exited`, { projectId, code, signal }),
    processStopped: (projectId) => this.log("info", "process-manager", `Process stopped`, { projectId }),
    processNotFound: (projectId) => this.log("warn", "process-manager", `Process not found for project: ${projectId}`, { projectId }),
    processKilled: (projectId, signal) => this.log("info", "process-manager", `Process killed with signal: ${signal}`, {
      projectId,
      signal
    }),
    processCleanup: (projectId) => this.log("info", "process-manager", `Cleaning up process`, { projectId }),
    processListRetrieved: (count) => this.log("debug", "process-manager", `Retrieved ${count} running processes`, { count }),
    error: (message, error, context) => this.log("error", "process-manager", message, {
      error: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : void 0,
      ...context
    })
  };
  /**
   * Build events-specific logging methods
   */
  buildEvents = {
    eventReceived: (eventType, projectId, sessionId) => this.log("info", "build-events", `Received event: ${eventType}`, {
      eventType,
      projectId,
      sessionId
    }),
    eventProcessed: (eventType, projectId) => this.log("debug", "build-events", `Processed event: ${eventType}`, { eventType, projectId }),
    buildStarted: (projectId, sessionId) => this.log("info", "build-events", `Build started`, { projectId, sessionId }),
    buildCompleted: (projectId, sessionId, success) => this.log("info", "build-events", `Build ${success ? "completed" : "failed"}`, {
      projectId,
      sessionId,
      success
    }),
    portDetected: (projectId, port) => this.log("info", "build-events", `Port detected: ${port}`, { projectId, port }),
    devServerStarted: (projectId, port, url) => this.log("info", "build-events", `Dev server started: ${url}`, { projectId, port, url }),
    devServerError: (projectId, error) => this.log("error", "build-events", `Dev server error: ${error}`, { projectId, error }),
    toolCallReceived: (toolName, toolId) => this.log("debug", "build-events", `Tool call: ${toolName}`, { toolName, toolId }),
    logChunkReceived: (projectId, chunkSize) => this.log("debug", "build-events", `Log chunk received`, { projectId, chunkSize }),
    invalidEvent: (reason) => this.log("warn", "build-events", `Invalid event: ${reason}`, { reason }),
    error: (message, error, context) => this.log("error", "build-events", message, {
      error: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : void 0,
      ...context
    })
  };
};
var buildLogger = new BuildLogger();
export {
  buildLogger
};
//# sourceMappingURL=build-logger.js.map